// offer-links.ts · Fuente de verdad de las URLs GHL de venta/checkout/oferta
// de los productos Robotea. TODAS las ventas pasan por GoHighLevel (NO Stripe).
//
// Owner: Clara Vega · 2026-06-09 · F-CAMPUS-UPSELL-GHL-001
//
// Tres tipos de enlace por tier:
//   - sales    → página de venta regular (precio completo, página de venta).
//   - checkout → checkout directo a precio completo (salta la página de venta).
//   - especial → checkout con la OFERTA del 20% desbloqueada por hito.
//
// La oferta -especial (20%) se desbloquea por HITO (lo resuelve el backend):
//   - basic_offer_unlocked = el alumno completó Robotea Start EN REAL y NO tiene Basic.
//   - pro_offer_unlocked   = el alumno completó Robotea Basic y NO tiene Pro.
//
// IMPORTANTE · 'auto' (Robotea Auto): se guarda aquí como fuente de verdad
// pero NO se usa todavía en la UI del campus (irá al campus en el futuro).
// No enlazar 'auto' desde componentes de momento.

export type OfferTier = 'auto' | 'basic' | 'pro';

export interface TierOfferLinks {
  /** Página de venta regular (precio completo). */
  sales: string;
  /** Checkout directo a precio completo (sin pasar por la página de venta). */
  checkout: string;
  /** Checkout con la oferta especial del 20% (desbloqueada por hito). */
  especial: string;
}

/**
 * URLs GHL canónicas por tier. Esta es la ÚNICA fuente de verdad de estos
 * enlaces en el frontend del campus.
 */
export const OFFER_LINKS: Record<OfferTier, TierOfferLinks> = {
  // Robotea Auto · NO usar en UI todavía (futuro).
  auto: {
    sales: 'https://robotea.io/auto',
    checkout: 'https://robotea.io/auto-unete',
    especial: 'https://robotea.io/auto-especial',
  },
  basic: {
    sales: 'https://robotea.io/basic',
    checkout: 'https://robotea.io/basic-unete',
    especial: 'https://robotea.io/basic-especial',
  },
  pro: {
    sales: 'https://robotea.io/pro',
    checkout: 'https://robotea.io/pro-unete',
    especial: 'https://robotea.io/pro-especial',
  },
};

/** Etiqueta legible del producto por tier (para CTAs). */
export const TIER_LABEL: Record<OfferTier, string> = {
  auto: 'Auto',
  basic: 'Basic',
  pro: 'Pro',
};

/**
 * Mapea el slug de curso del campus al tier de oferta GHL.
 * Devuelve `null` para cursos que no tienen oferta GHL asociada (p. ej.
 * 'robotea-start' o 'roboteam', que siguen su propio flujo).
 */
export function tierFromCourseSlug(slug: string): OfferTier | null {
  switch (slug) {
    case 'robotea-basic':
      return 'basic';
    case 'robotea-pro':
      return 'pro';
    case 'robotea-auto':
      return 'auto';
    default:
      return null;
  }
}

export interface OfferLinkOptions {
  /**
   * `true` si la oferta del 20% de ESE tier está desbloqueada por hito
   * (basic_offer_unlocked / pro_offer_unlocked del snapshot XP del backend).
   * Si es `true` se devuelve la URL -especial (20%); si no, la página de venta.
   */
  unlocked?: boolean;
}

/**
 * Devuelve la URL GHL adecuada para un tier:
 *   - Si la oferta está desbloqueada (`unlocked`) → checkout -especial (20%).
 *   - Si no → página de venta regular (`sales`).
 *
 * Para el checkout directo a precio completo (sin oferta) usa
 * `OFFER_LINKS[tier].checkout` directamente.
 */
export function offerLinkFor(tier: OfferTier, opts: OfferLinkOptions = {}): string {
  const links = OFFER_LINKS[tier];
  return opts.unlocked ? links.especial : links.sales;
}
